import { useEffect, useState } from "react";
import { callFunction, collection, db, getDocs, query, where } from "../boardData";
import { ladderReportRows } from "./stormUi";

// All Storm Center widgets on a board read the same feed: the getstormdata
// callable (admindashboard functions/stormCenter.js), which aggregates NEXRAD
// storm cells, NWS/mPING storm reports and NWS alerts around the station.
//
// Widgets asking for the same agency/location/radius share one request and one
// poll, so a four-widget Storm Center screen costs one call a minute, not four.
// Reports are always fetched for REQUEST_HOURS and each widget trims them to
// its own window.

const POLL_MS = 60 * 1000;
const REQUEST_HOURS = 6;

const feeds = new Map(); // key -> { state, listeners:Set, timer, inflight }

function feedKey({ agency, lat, lng, radiusMi }) {
  return `${agency}|${lat.toFixed(3)}|${lng.toFixed(3)}|${radiusMi}`;
}

// An older getstormdata doesn't include the Ladder app's hazard reports (its
// `sources` has no `ladder`). Until it is redeployed, the board reads them
// itself: boards may read WXREPORTS, and the rows come out the same.
async function withLadderReports(data, params) {
  if (!data || !data.sources || data.sources.ladder !== undefined) return data;
  const now = Date.now();
  try {
    const snap = await getDocs(
      query(collection(db, "WXREPORTS"), where("timestamp", ">=", new Date(now - REQUEST_HOURS * 3600 * 1000)))
    );
    const rows = ladderReportRows(
      snap.docs.map((d) => ({ id: d.id, ...d.data() })),
      { lat: params.lat, lng: params.lng, radiusMi: params.radiusMi, hours: REQUEST_HOURS, now }
    );
    return {
      ...data,
      reports: (data.reports || []).concat(rows).sort((a, b) => (b.timeMs || 0) - (a.timeMs || 0)),
      sources: { ...data.sources, ladder: { ok: true, count: rows.length, fromBoard: true } },
    };
  } catch (err) {
    console.warn("Ladder hazard reports unavailable", err);
    return { ...data, sources: { ...data.sources, ladder: { ok: false, error: "unavailable" } } };
  }
}

async function load(key, params) {
  const feed = feeds.get(key);
  if (!feed || feed.inflight) return;
  feed.inflight = true;
  try {
    const { data: served } = await callFunction("getstormdata", { ...params, hours: REQUEST_HOURS });
    const data = await withLadderReports(served, params);
    feed.state = { data, error: null, loading: false, updatedAt: Date.now() };
  } catch (err) {
    // Keep the last good picture on screen; just flag that it is stale.
    console.warn("getstormdata failed", err);
    feed.state = { ...feed.state, error: err, loading: false };
  } finally {
    feed.inflight = false;
    feed.listeners.forEach((fn) => fn(feed.state));
  }
}

function subscribe(params, listener) {
  const key = feedKey(params);
  let feed = feeds.get(key);
  if (!feed) {
    feed = {
      state: { data: null, error: null, loading: true, updatedAt: 0 },
      listeners: new Set(),
      timer: null,
      inflight: false,
    };
    feeds.set(key, feed);
  }
  feed.listeners.add(listener);
  listener(feed.state);
  if (!feed.timer) {
    const stale = Date.now() - (feed.state.updatedAt || 0) > POLL_MS / 2;
    if (stale) load(key, params);
    feed.timer = setInterval(() => load(key, params), POLL_MS);
  }
  return () => {
    feed.listeners.delete(listener);
    if (feed.listeners.size === 0) {
      clearInterval(feed.timer);
      feed.timer = null;
    }
  };
}

export function useStormData({ agency, lat, lng, radiusMi = 75 }) {
  const ready = !!agency && Number.isFinite(lat) && Number.isFinite(lng);
  const radius = Number(radiusMi) || 75;
  const [state, setState] = useState({ data: null, error: null, loading: ready, updatedAt: 0 });

  useEffect(() => {
    if (!ready) return undefined;
    return subscribe({ agency, lat, lng, radiusMi: radius }, setState);
  }, [ready, agency, lat, lng, radius]);

  return { ...state, ready };
}

// Test hook: forget every shared feed.
export function __resetStormFeeds() {
  feeds.forEach((f) => clearInterval(f.timer));
  feeds.clear();
}
